import type { ReactElement } from 'react';
import { useCallback, useEffect, useState } from 'react';
import type { ComplianceStatusResponse } from '@fetmode/types';
import { t } from '@fetmode/i18n';
import { Link, useLocation } from 'react-router-dom';

import { useAuth } from '../auth/index.js';
import './AgeVerifyBanner.css';

export interface AgeVerifyBannerProps {
  className?: string;
}

export function AgeVerifyBanner({ className }: AgeVerifyBannerProps): ReactElement | null {
  const { api, user } = useAuth();
  const location = useLocation();
  const [status, setStatus] = useState<ComplianceStatusResponse | null>(null);

  const loadStatus = useCallback(async () => {
    const result = await api.getComplianceStatus();
    setStatus(result);
  }, [api]);

  useEffect(() => {
    if (!user) {
      setStatus(null);
      return;
    }

    void loadStatus().catch(() => {
      setStatus(null);
    });
  }, [loadStatus, user, location.pathname]);

  useEffect(() => {
    if (!user) {
      return;
    }

    function onVisible(): void {
      if (document.visibilityState === 'visible') {
        void loadStatus().catch(() => {
          setStatus(null);
        });
      }
    }

    document.addEventListener('visibilitychange', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [loadStatus, user]);

  if (!user || !status || status.canAccess || status.action !== 'verify') {
    return null;
  }

  const rootClass = className ? `age-verify-banner ${className}` : 'age-verify-banner';

  return (
    <div className={rootClass} role="region" aria-label={t('common.compliance.verifyBannerLabel')}>
      <div className="age-verify-banner__icon" aria-hidden>
        ✓
      </div>
      <div className="age-verify-banner__copy">
        <p className="age-verify-banner__title">{t('common.compliance.verifyBannerTitle')}</p>
        <p className="age-verify-banner__hint">{t('common.compliance.verifyBannerHint')}</p>
      </div>
      <Link to="/verify" className="age-verify-banner__cta">
        {t('common.compliance.verifyBannerCta')}
      </Link>
    </div>
  );
}
